/**
 * Client-side passkey registration — wraps @simplewebauthn/browser.
 *
 * Bundled into app.js via esbuild (IIFE). The startRegistration function
 * handles the browser's navigator.credentials.create() call and UI prompts.
 */

import { startRegistration } from '@simplewebauthn/browser';

export interface PasskeyResult {
  identityHash: string;
  identityField: string;
  credentialId: string;
}

/**
 * Register a new passkey and return the identity field for ZK proof generation.
 *
 * Flow:
 *   1. POST /api/passkey/register-options → WebAuthn creation options
 *   2. startRegistration() → browser biometric/passkey prompt
 *   3. POST /api/passkey/register with credential → identityField
 */
export async function registerPasskey(): Promise<PasskeyResult> {
  // 1. Get registration options from server
  const optionsResp = await fetch('/api/passkey/register-options', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!optionsResp.ok) {
    const err = await optionsResp.json().catch(() => ({ error: optionsResp.statusText }));
    throw new Error(err.error || `HTTP ${optionsResp.status}`);
  }
  const { options, timestamp, userHandle } = await optionsResp.json();

  // 2. Browser prompts user for biometric/passkey creation
  const credential = await startRegistration({ optionsJSON: options });

  // 3. Verify with server and get identity field
  const verifyResp = await fetch('/api/passkey/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential, timestamp, userHandle }),
  });
  if (!verifyResp.ok) {
    const err = await verifyResp.json().catch(() => ({ error: verifyResp.statusText }));
    throw new Error(err.error || `HTTP ${verifyResp.status}`);
  }

  const result = await verifyResp.json();
  return {
    identityHash: result.identityHash,
    identityField: result.identityField,
    credentialId: result.credentialId,
  };
}
